/**
 * Saved analyses (FR-PIL-08): an analysis of the catalogue or a pivot, kept with its period rule
 * under a name by a user, private or shared with the site or the group, and schedulable (the
 * monthly e-mail of FR-PIL-05 can target a saved analysis).
 */
import { z } from 'zod';
import { ANALYSIS_KEYS } from './analyses.js';
import { PIVOT_DIMENSIONS, PIVOT_MEASURES } from './pivot.js';
export const SAVED_ANALYSIS_KINDS = ['CATALOGUE', 'PIVOT'];
/** PRIVATE: its owner only; SITE: whoever reads the site; GROUP: whoever reads the group too. */
export const SAVED_ANALYSIS_SHARING = ['PRIVATE', 'SITE', 'GROUP'];
/** The period follows the calendar when the analysis is opened, or stays fixed. */
export const PERIOD_MODES = [
    'YEAR_TO_DATE',
    'THIS_MONTH',
    'LAST_MONTH',
    'LAST_QUARTER',
    'LAST_12_MONTHS',
    'LAST_YEAR',
    'FIXED',
];
const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
export const savedPeriodSchema = z
    .object({
    mode: z.enum(PERIOD_MODES),
    from: isoDate.optional(),
    to: isoDate.optional(),
})
    .refine((p) => p.mode !== 'FIXED' || (p.from && p.to && p.from <= p.to), {
    message: 'A fixed period needs its first and last days',
});
const groupFilters = {
    companyId: z.string().uuid().optional(),
    siteIds: z.array(z.string().uuid()).max(200).optional(),
};
export const savedDefinitionSchema = z.discriminatedUnion('kind', [
    z.object({
        kind: z.literal('CATALOGUE'),
        analysisKey: z.enum(ANALYSIS_KEYS),
        groupBy: z.string().max(40).optional(),
        compare: z.boolean().default(true),
        ...groupFilters,
    }),
    z.object({
        kind: z.literal('PIVOT'),
        rows: z.enum(PIVOT_DIMENSIONS),
        columns: z.enum(PIVOT_DIMENSIONS).optional(),
        measures: z.array(z.enum(PIVOT_MEASURES)).min(1).max(4),
        chart: z.enum(['table', 'bars', 'columns']).default('columns'),
        ...groupFilters,
    }),
]);
export const savedAnalysisSchema = z.object({
    name: z.string().trim().min(1).max(120),
    definition: savedDefinitionSchema,
    period: savedPeriodSchema,
    sharing: z.enum(SAVED_ANALYSIS_SHARING).default('PRIVATE'),
});
export const savedAnalysisPatchSchema = savedAnalysisSchema.partial();
const iso = (d) => d.toISOString().slice(0, 10);
const utc = (y, m, d) => new Date(Date.UTC(y, m, d));
/** The first and last day a period rule covers on a given day. */
export function resolvePeriod(period, today) {
    const y = Number(today.slice(0, 4));
    const m = Number(today.slice(5, 7)) - 1;
    switch (period.mode) {
        case 'FIXED':
            return { from: period.from, to: period.to };
        case 'YEAR_TO_DATE':
            return { from: `${y}-01-01`, to: today };
        case 'THIS_MONTH':
            return { from: iso(utc(y, m, 1)), to: today };
        case 'LAST_MONTH':
            return { from: iso(utc(y, m - 1, 1)), to: iso(utc(y, m, 0)) };
        case 'LAST_QUARTER': {
            const q = Math.floor(m / 3) * 3; // first month of the current quarter
            return { from: iso(utc(y, q - 3, 1)), to: iso(utc(y, q, 0)) };
        }
        case 'LAST_12_MONTHS':
            return { from: iso(utc(y, m - 11, 1)), to: today };
        case 'LAST_YEAR':
            return { from: `${y - 1}-01-01`, to: `${y - 1}-12-31` };
    }
}
export const PERIOD_MODE_LABELS = {
    YEAR_TO_DATE: "Depuis le début de l'année",
    THIS_MONTH: 'Le mois en cours',
    LAST_MONTH: 'Le mois dernier',
    LAST_QUARTER: 'Le trimestre dernier',
    LAST_12_MONTHS: 'Les 12 derniers mois',
    LAST_YEAR: "L'année dernière",
    FIXED: 'Dates fixes',
};
//# sourceMappingURL=saved-analysis.js.map